import {
  $RolesPermissions,
  $RolesPermissionsStates,
  $User,
  $UserAddPermissions,
  ACTIONS,
  UserPriorityEnum,
  changeRolePermission,
  fetchRolesPermissions,
  getPermissionName,
  removeRole,
} from "@store/user-store";
import { Checkbox, FormControlLabel } from "@mui/material";
import { CheckboxCheckedSVG, CheckboxSVG } from "@components/svgs";
import { WrapperSVG } from "@components/WrapperSVG/WrapperSVG";
import { Tooltip } from "@mui/material";
import { TrashIcon } from "@phosphor-icons/react";
import { ErrorIndicator, Loader } from "@ui/indicators";
import { Fragment, memo, useEffect, useMemo, useState } from "react";

import RolesPermissionsService from "@services/roles-permissions-service";
import { UserDataT } from "@interfaces/user";
import clsx from "clsx";
import s from "@scss/pages/permissions.module.scss";
import { useLocation } from "react-router-dom";
import { useModal } from "@modals/index";
import { useUnit } from "effector-react";
import { useSelectedCompanyQuery } from "@lib/company";
import { setTitle } from "@store/title-store";

export const PermissionsRoles = memo(() => {
  const rolesNPermissions = useUnit($RolesPermissions);
  const { isFetched, isLoading, error, isFetchedCompany } = useUnit(
    $RolesPermissionsStates
  );
  const [selectedRole, setSelectedRole] = useState<any>(null);
  const permissions = useUnit($UserAddPermissions);
  const user = useUnit($User) as UserDataT;
  const location = useLocation();
  const IS_COMPANY_MODULE = location.pathname.includes("company");

  const { company } = useSelectedCompanyQuery();
  const { open } = useModal();

  useEffect(() => {
    setTitle([
      { title: "Главная", href: "/" },
      { title: "Настройки ЛК", href: "/settings/roles" },
      { title: "Роли", href: "/settings/roles" },
    ]);
  }, []);

  useEffect(() => {
    if (!selectedRole && rolesNPermissions?.roles[0]) {
      setSelectedRole({
        ...rolesNPermissions.roles[0],
        permissions: rolesNPermissions.roles[0].permissions.map(
          (perm: any) => perm.action_name
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolesNPermissions]);

  useEffect(() => {
    if (!company) return;

    const doesNeedToFetch =
      (!isFetchedCompany && !isLoading && IS_COMPANY_MODULE) ||
      (!isFetched && !isLoading && !IS_COMPANY_MODULE);

    if (doesNeedToFetch) {
      fetchRolesPermissions(
        IS_COMPANY_MODULE
          ? {
              exactCompany: true,
              companyId: company.id,
              priority: user.role.priority,
            }
          : { priority: user.role.priority }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  const handleDelete = (id: string, roleId?: number) => {
    RolesPermissionsService.RemoveRole(
      { roleId: id, replace: roleId },
      (err, res) => {
        removeRole({ roleId: res?.data.id });
      }
    );
  };

  const loadUsersRole = (role: { name: string; id: string }) => {
    RolesPermissionsService.GetAllUsersByRoles(
      { roleId: role.id },
      (err, res) => {
        if (res && res.data.length > 0) {
          open("RemoveRoleWithUsersModal", {
            btnText: "Удалить",
            modalData: {
              text: `Вы уверены, что хотите удалить роль "${role.name}"?`,
              textUsers: `В ней присутсвует ${res.data.length} пользователей!`,
              roles: rolesNPermissions?.roles,
              id: role.id,
              onConfirm: handleDelete,
            },
          });
        } else {
          open("ConfirmActionModal", {
            btnText: "Удалить",
            modalData: {
              text: `Вы уверены, что хотите удалить роль "${role.name}"?`,
            },
            onConfirm: () => handleDelete(role.id),
          });
        }
      }
    );
  };

  const rolesToRender = useMemo(() => {
    if (!rolesNPermissions) return null;
    const rolesByPriority: any = [];
    rolesNPermissions.roles.forEach((role) => {
      if (!rolesByPriority[role.priority]) rolesByPriority[role.priority] = [];
      rolesByPriority[role.priority].push(role);
    });

    return rolesByPriority.map((rolesGrop: any, index: number) => {
      return (
        <Fragment key={index}>
          <p className={s.level}>{index} уровень:</p>
          {rolesGrop.map((role: any) => {
            return (
              <button
                key={role.id}
                className={clsx(s.role, {
                  [s.role_selected]: role?.name === selectedRole?.name,
                })}
                onClick={() => changeRole(role)}
              >
                {role.name}
                {role.id !== 777 && role.id !== 1 && (
                  <WrapperSVG color="red" float>
                    <Tooltip title="Удалить" placement="top">
                      <TrashIcon size={24} onClick={() => loadUsersRole(role)} />
                    </Tooltip>
                  </WrapperSVG>
                )}
              </button>
            );
          })}
        </Fragment>
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolesNPermissions, rolesNPermissions?.roles.length, selectedRole]);

  if (error) return <ErrorIndicator />;
  if (isLoading) return <Loader />;
  if (
    (!isFetched && !IS_COMPANY_MODULE) ||
    (!isFetchedCompany && IS_COMPANY_MODULE)
  )
    return <Loader />;

  const changeRole = (role: any) => {
    setSelectedRole({
      ...role,
      permissions: role.permissions.map((perm: any) => perm.action_name),
    });
  };

  const changePermission = (permission: any) => {
    changeRolePermission({
      roleId: selectedRole.id,
      permission: permission,
    });
    RolesPermissionsService.ChangeRolePermissions(
      {
        roleId: selectedRole.id,
        permissionId: permission.id,
      },
      (err, res) => {
        if (res) {
          const existingPermission = selectedRole.permissions.find(
            (perm: any) => perm === permission.action_name
          );

          if (existingPermission) {
            const filteredPermissions = selectedRole.permissions.filter(
              (perm: any) => perm !== existingPermission
            );
            return setSelectedRole((prev: any) => ({
              ...prev,
              permissions: filteredPermissions,
            }));
          } else {
            return setSelectedRole((prev: any) => ({
              ...prev,
              permissions: [...prev.permissions, permission.action_name],
            }));
          }
        }
      }
    );
  };

  const filterPermissions = (permission: any) => {
    if (
      !permissions.hasRequiredLevel(UserPriorityEnum.SuperAdmin) &&
      [
        ACTIONS.roles_levelFirstRoles_allowedToCreate,
        ACTIONS.roles_levelFirstRoles_allowedToEdit,
        ACTIONS.roles_levelFirstRoles_allowedToAssign,
      ].includes(permission.action_name)
    )
      return false;
    if (
      !permissions.hasRequiredLevel(UserPriorityEnum.Admin) &&
      [
        ACTIONS.roles_levelFirstRoles_allowedToCreate,
        ACTIONS.roles_levelFirstRoles_allowedToEdit,
        ACTIONS.roles_levelFirstRoles_allowedToAssign,
        ACTIONS.roles_levelSecondRoles_allowedToCreate,
        ACTIONS.roles_levelSecondRoles_allowedToEdit,
        ACTIONS.roles_levelSecondRoles_allowedToAssign,
      ].includes(permission.action_name)
    )
      return false;

    return true;
  };

  const groupsPermissions = (groups: any, permission: any) => {
    let title: string | null = null;
    let slug: string = permission.action_name.split("_")[0];
    switch (slug) {
      case "personalAccount":
        title = "Личный кабинет";
        break;
      case "users":
        title = "Пользователи";
        break;
      case "roles":
        title = "Роли";
        break;
      case "companies":
        title = "Компании";
        break;
      case "companyWorkspace":
        title = "Компания - Рабочее пространство";
        break;
      case "companyDocumentTemplates":
        title = "Компания - Шаблоны документов";
        break;
      case "companyStaffing":
        title = "Компания - Штатное расписание";
        break;
      case "companyEventsCalendar":
        title = "Компания - Календарь событий";
        break;
      case "companySpecialistWorkPlan":
        title = "Компания - План работы специалиста";
        break;
      case "companyDocumentSigningControl":
        title = "Компания - Контроль подписания документов";
        break;
      case "companyPrescriptions":
        title = "Компания - Предписания";
        break;
      case "companyProcedureControls":
        title = "Компания - Контроль выполнения процедур";
        break;
      case "companyApplicationsForTraining":
        title = "Компания - Заявки на обучение";
        break;
      case "companyDepartureSchedule":
        title = "Компания - График выездов";
        break;
      case "projectNews":
        title = "Новости проекта";
        break;
      case "educationalCenter":
        title = "Учебный центр";
        break;
      case "educationalCenterInternal":
        title = "Учебный центр - Внутреннее обучение";
        break;
      case "developmentNews":
        title = "Скоро в Личном кабинете";
        break;
      case "legislationNews":
        title = "Новости законодательства";
        break;
      case "usefulMaterials":
        title = "Полезные материалы";
        break;
      case "responsibility":
        title = "Ответственность";
        break;
      case "legalInformation":
        title = "Юридическая информация";
        break;
      case "judicialPractice":
        title = "Судебная практика";
        break;
      case "payment":
        title = "Оплата";
        break;
      case "specialistHelp":
        title = "Помощь специалиста";
        break;
      case "directories":
        title = "Справочники";
        break;
      case "support":
        title = "Техподдержка";
        break;
      case "questionAnswer":
        title = "Вопрос ответ";
        break;
      case "documentTemplates":
        title = "Шаблоны документов";
        break;
      default:
        title = "Прочее (не распределено)";
    }
    if (!groups[slug]) {
      groups[slug] = {
        title: title,
        slug: slug,
        permissions: [{ ...permission }],
      };
    } else {
      groups[slug].permissions.push(permission);
    }
    return groups;
  };

  return (
    <>
      {(permissions.hasRequiredLevel(UserPriorityEnum.Admin) ||
        permissions.hasPermission(ACTIONS.roles_allowedToShow)) && (
        <div className={s.content}>
          <div className={s.roles}>{rolesToRender}</div>
          <div className={s.devider}></div>
          <div className={s.permissions}>
            <p className="font-xl">
              {selectedRole?.name ? selectedRole.name : ""}
            </p>
            {selectedRole &&
              Object.values(
                rolesNPermissions?.permissions
                  .filter((permission: any) => filterPermissions(permission))
                  .reduce(groupsPermissions, [])
              ).map((group: any, index: number) => {
                return (
                  <div key={index}>
                    <div className="mt-30 mb-5 font-lg">{group.title}:</div>
                    {group.permissions &&
                      group.permissions.map(
                        (permission: any, index: number) => {
                          const included = selectedRole?.permissions[0]
                            ? selectedRole?.permissions.includes(
                                permission.action_name
                              )
                            : false;

                          return (
                            <div>
                              <FormControlLabel
                                key={index * Math.random()}
                                control={
                                  <Checkbox
                                    checked={included}
                                    name="isFinancial"
                                    color="primary"
                                    icon={<CheckboxSVG />}
                                    checkedIcon={<CheckboxCheckedSVG />}
                                    onChange={() =>
                                      changePermission(permission)
                                    }
                                  />
                                }
                                label={
                                  getPermissionName(permission.action_name) ||
                                  permission.action_name
                                }
                              />
                            </div>
                          );
                        }
                      )}
                  </div>
                );
                // const included = selectedRole?.permissions[0]
                //   ? selectedRole?.permissions.includes(permission.action_name)
                //   : false;

                // return (
                //   <div key={permission.id}>
                //     {title && (
                //       <div className="mt-30 mb-5 font-lg">{title}:</div>
                //     )}
                //     <FormControlLabel
                //       key={index * Math.random()}
                //       control={
                //         <Checkbox
                //           checked={included}
                //           name="isFinancial"
                //           color="primary"
                //           icon={<CheckboxSVG />}
                //           checkedIcon={<CheckboxCheckedSVG />}
                //           onChange={() => changePermission(permission)}
                //         />
                //       }
                //       label={getPermissionName(permission.action_name) || permission.action_name}
                //     />
                //   </div>
                // );
              })}
          </div>
        </div>
      )}
    </>
  );
});
